import { EFFICIENCY_TIERS, type EfficiencyTier, efficiencyAt, wallWatts } from './efficiency'
import { MONITOR_WATTS } from './parts'
import type { LoadEstimate } from './power'
import { isValidPrice, type Usage } from './spec'

/**
 * What a build costs to run.
 *
 * A day is split in two: heavy hours (gaming, rendering) at the build's full
 * estimated load, and the rest of the time on at its light-use draw. Each passes
 * through the power supply at that load's efficiency; monitors are added at the
 * wall, as they never draw from the PSU:
 *
 *   kWh/day = (P_heavy / η_heavy × heavy hours
 *            + P_light / η_light × light hours
 *            + P_monitors × hours on) / 1000
 */

export const DAYS_PER_YEAR = 365

export interface EnergyEstimate {
  /** PSU efficiency at the full load and at the light-use load. */
  readonly heavyEfficiency: number
  readonly lightEfficiency: number
  /** Drawn from the wall by the computer alone, monitors excluded. */
  readonly heavyWallWatts: number
  readonly lightWallWatts: number
  readonly monitorWatts: number
  /** Efficiency averaged over the hours the computer is on. */
  readonly averageEfficiency: number
  readonly kwhPerDay: number
  readonly kwhPerYear: number
  /** `null` when no usable electricity price was given. */
  readonly costPerYear: number | null
}

export function estimateEnergy(
  load: LoadEstimate,
  psuWatts: number,
  usage: Usage,
  tier: EfficiencyTier = usage.efficiency,
): EnergyEstimate {
  const hours = usage.hoursPerDay
  // The pickers keep heavy hours within hours on; a hand-edited link might not.
  const heavyHours = Math.min(usage.heavyHoursPerDay, hours)
  const lightHours = hours - heavyHours

  const heavyEfficiency = efficiencyAt(tier, load.peakWatts / psuWatts)
  const lightEfficiency = efficiencyAt(tier, load.lightWatts / psuWatts)
  const heavyWallWatts = wallWatts(load.peakWatts, heavyEfficiency)
  const lightWallWatts = wallWatts(load.lightWatts, lightEfficiency)
  const monitorWatts = MONITOR_WATTS[usage.monitors.type] * usage.monitors.count

  const wattHoursPerDay =
    heavyWallWatts * heavyHours + lightWallWatts * lightHours + monitorWatts * hours
  const kwhPerDay = wattHoursPerDay / 1000
  const kwhPerYear = kwhPerDay * DAYS_PER_YEAR

  return {
    heavyEfficiency,
    lightEfficiency,
    heavyWallWatts,
    lightWallWatts,
    monitorWatts,
    averageEfficiency: averageEfficiency(heavyEfficiency, heavyHours, lightEfficiency, lightHours),
    kwhPerDay,
    kwhPerYear,
    costPerYear: isValidPrice(usage.pricePerKwh) ? kwhPerYear * usage.pricePerKwh : null,
  }
}

/**
 * The two efficiencies weighted by the time spent at each:
 *
 *   η_avg = η_heavy × heavy hours / hours on + η_light × light hours / hours on
 */
export function averageEfficiency(
  heavyEfficiency: number,
  heavyHours: number,
  lightEfficiency: number,
  lightHours: number,
): number {
  const hours = heavyHours + lightHours
  if (hours <= 0) return lightEfficiency
  return (heavyEfficiency * heavyHours + lightEfficiency * lightHours) / hours
}

export interface TierCost {
  readonly tier: EfficiencyTier
  readonly kwhPerYear: number
  readonly costPerYear: number | null
}

/** The same build and usage under every 80 PLUS tier, least efficient first. */
export function compareTiers(load: LoadEstimate, psuWatts: number, usage: Usage): TierCost[] {
  return EFFICIENCY_TIERS.map((tier) => {
    const energy = estimateEnergy(load, psuWatts, usage, tier)
    return { tier, kwhPerYear: energy.kwhPerYear, costPerYear: energy.costPerYear }
  })
}

/**
 * Years for a more efficient unit to repay its higher price:
 *
 *   payback = (price of unit − price of baseline) / (yearly cost of baseline − yearly cost of unit)
 *
 * `null` when it never does — it saves nothing, or costs no more to begin with.
 */
export function paybackYears(extraPrice: number, yearlySaving: number): number | null {
  if (!(yearlySaving > 0) || !(extraPrice > 0)) return null
  return extraPrice / yearlySaving
}
